// src/pages/jewellers/CustomerKittyForm.js
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../api";
import Loader from "../../components/Loader";

export default function CustomerKittyForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const user = JSON.parse(localStorage.getItem("user"));
  const userId = user?.id;

  

  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    customer_id: "",
    kitty_id: "",
    //date: "",
    date: new Date().toISOString().split("T")[0],
    target_amount: "",
    remark: "",
    userId: userId,
  });
  const [paymentScreenshot, setPaymentScreenshot] = useState(null);

  const [customers, setCustomers] = useState([]);
  const [kitties, setKitties] = useState([]);
  const [filteredKitties, setFilteredKitties] = useState([]);
  const [errors, setErrors] = useState({});

  // Fetch customers and all kitties
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [customersRes, kittiesRes] = await Promise.all([
          api.get("/jeweller-customers"),
          api.get("/kitties"),
        ]);
        setCustomers(customersRes.data);
        setKitties(kittiesRes.data.data || kittiesRes.data);

        if (isEdit) {
          const res = await api.get(`/customer-kitties/view/${id}`);
          const data = res.data;
          setForm({
            ...data,
            customer_id: data.customer_id || "",
            kitty_id: data.kitty_id || "",
            date: data.date || "",
            target_amount: data.target_amount || "",
            remark: data.remark || "",
            userId: userId,
          });
          
          // If editing, fetch kitties for the customer
          if (data.customer_id) {
            fetchCustomerKitties(data.customer_id);
          }
        }
      } catch (err) {
        console.error("Failed to fetch data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, isEdit]);

  // Function to fetch customer-specific kitties
  const fetchCustomerKitties = async (customerId) => {
    try {
      const response = await api.get(`/customer-wise-kitty/${customerId}/${userId}`);
      const kittiesData = response.data.data || response.data;
      setFilteredKitties(kittiesData);
      
      if (isEdit && form.kitty_id && kittiesData.length > 0) {
        const selectedKitty = kittiesData.find(k => String(k.id) === String(form.kitty_id));
        if (selectedKitty) {
          setForm(prev => ({
            ...prev,
            target_amount: selectedKitty.target_amount || ""
          }));
        }
      }
    } catch (err) {
      console.error("Failed to fetch customer kitties:", err);
      setFilteredKitties([]);
    }
  };


  // Handle customer change
  const handleCustomerChange = (e) => {
    const customerId = e.target.value;
    
    setForm((prev) => ({ 
      ...prev, 
      customer_id: customerId,
      kitty_id: "",
      target_amount: ""
    }));

    if (customerId) {
      fetchCustomerKitties(customerId);
    } else {
      setFilteredKitties([]);
    }
  };

  // Handle kitty change
  const handleKittyChange = (e) => {
    const kittyId = e.target.value;
    const selectedKitty = filteredKitties.find(k => String(k.id) === kittyId);
    
    setForm((prev) => ({ 
      ...prev, 
      kitty_id: kittyId,
      target_amount: selectedKitty ? selectedKitty.target_amount || "" : ""
    }));
  };

  // Handle text/select inputs
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // Handle file input separately
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPaymentScreenshot(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    
    // Create FormData to handle file upload
    const formData = new FormData();
    
    // Append all form fields
    formData.append('customer_id', form.customer_id);
    formData.append('kitty_id', form.kitty_id);
    formData.append('target_amount', form.target_amount);
    formData.append('date', form.date);
    formData.append('remark', form.remark || '');
    formData.append('userId', form.userId);
    
    // Append file if exists
    if (paymentScreenshot) {
      formData.append('payment_screenshot', paymentScreenshot);
    }
    
    try {
      if (isEdit) {
        // For editing, use regular JSON (or adjust if your edit endpoint supports files)
        await api.put(`/customer-kitties/${id}`, form);
      } else {
        // For creating, use FormData with proper headers
        await api.post("/customer-payment", formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }
      navigate("/jewellers/CustomerPayment");
    } catch (err) {
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      } else {
        console.error("Save failed:", err);
        alert("Failed to save. Please check all required fields.");
      }
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="p-6">
      <h1 className="text-xl font-bold mb-4">
        {isEdit ? "Edit Customer Kitty" : "Customer Payment Add"}
      </h1>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Customer Select */}
        <div>
          <label className="block mb-1 font-medium">Customer</label>
          <select
            name="customer_id"
            value={form.customer_id}
            onChange={handleCustomerChange}
            required
            className="w-full border rounded px-3 py-2"
          >
            <option value="">-- Select Customer --</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.full_name}
              </option>
            ))}
          </select>
          {errors.customer_id && (
            <p className="text-red-600">{errors.customer_id[0]}</p>
          )}
        </div>

        {/* Kitty Select */}
        <div>
          <label className="block mb-1 font-medium">Kitty</label>
          <select
            name="kitty_id"
            value={form.kitty_id}
            onChange={handleKittyChange}
            required
            className="w-full border rounded px-3 py-2"
            disabled={!form.customer_id}
          >
            <option value="">-- Select Kitty --</option>
            {filteredKitties.length > 0 ? (
              filteredKitties.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.kitty_name} ({k.kitty_type})
                </option>
              ))
            ) : form.customer_id ? (
              <option value="" disabled>
                No kitties available for this customer
              </option>
            ) : (
              <option value="" disabled>
                Please select a customer first
              </option>
            )}
          </select>
          {errors.kitty_id && (
            <p className="text-red-600">{errors.kitty_id[0]}</p>
          )}
        </div>

        {/* Target Amount */}
        <div>
          <label className="block mb-1 font-medium">Target Amount</label>
          <input
            type="number"
            name="target_amount"
            value={form.target_amount}
            onChange={handleChange}
            required
            className="w-full border rounded px-3 py-2 bg-gray-100"
          />
        </div>

        {/* Date and Screenshot */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block mb-1 font-medium">Payment Date</label>
            <input
              type="date"
              name="date"
              value={form.date}
              onChange={handleChange}
              required
              className="w-full border rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block mb-1 font-medium">Payment Screenshot</label>
            <input
              type="file"
              name="payment_screenshot"
              onChange={handleFileChange}
              accept="image/png, image/jpg, image/jpeg"
              required={!isEdit} 
              className="w-full border rounded px-3 py-2"
            />
            <p className="text-sm text-gray-500 mt-1">
              Upload payment screenshot (PNG, JPG, JPEG)
            </p>
          </div>
        </div>

        {/* Remark */}
        <div>
          <label className="block mb-1 font-medium">Remark</label>
          <textarea
            name="remark"
            value={form.remark}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2"
            rows="3"
          />
        </div>

        <button
          type="submit"
          className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
        >
          {isEdit ? "Update Kitty" : "Add Payment"}
        </button>
      </form>
    </div>
  );
}